import {Matrix4,Vector3} from 'three';
import {validJointCorners,type JointCorners} from '../../../../shared/roomCornerPreference';
import type {PhotoPoint,RoomWalls,WallCamera} from './photo-walls';
const separation=(a:PhotoPoint,b:PhotoPoint)=>Math.hypot(a.x-b.x,a.y-b.y);
/** Two observed parallel side-floor edges and two vertical seams recover the
 * lens. The connecting back-floor edge is held out to check orthogonality.
 * This assumes a centered rectilinear photo and perpendicular walls. */
export function jointRoomCamera(j:JointCorners,aspect:number,selected:PhotoPoint):WallCamera|null{
 if(!validJointCorners(j)||!Number.isFinite(aspect)||aspect<=0)return null;
 const p=(v:PhotoPoint)=>new Vector3((v.x/100-.5)*aspect,.5-v.y/100,1);
 const [l,r,lf,rf,lu,ru]=[j.leftCorner,j.rightCorner,j.leftFloor,j.rightFloor,j.leftUpper,j.rightUpper].map(p);
 if(r.x-l.x<.12||lu.y<=l.y||ru.y<=r.y)return null;
 const segments=[[l,lf],[r,rf],[l,lu],[r,ru],[l,r]];
 if(segments.some(([a,b])=>a.distanceTo(b)<.08))return null;
 const intersection=(a:Vector3,b:Vector3,c:Vector3,d:Vector3)=>new Vector3().crossVectors(new Vector3().crossVectors(a,b),new Vector3().crossVectors(c,d));
 const solve=(a:Vector3,b:Vector3,c:Vector3,d:Vector3,e:Vector3,g:Vector3)=>{
  const vz=intersection(a,c,b,d),vy=intersection(a,e,b,g);
  if(Math.abs(vz.z)<1e-7||Math.abs(vy.z)<1e-7)return null;
  vz.divideScalar(vz.z);vy.divideScalar(vy.z);
  const f2=-(vz.x*vy.x+vz.y*vy.y);if(f2<.0625||f2>6.25)return null;
  const focal=Math.sqrt(f2),z=new Vector3(vz.x,vz.y,-focal).normalize(),y=new Vector3(vy.x,vy.y,-focal).normalize();
  const ray=(axis:Vector3,at:Vector3)=>new Vector3(focal*axis.x+at.x*axis.z,focal*axis.y+at.y*axis.z,0).normalize();
  if(ray(y,a).dot(e.clone().sub(a))<0)y.negate();
  if(ray(z,a).dot(c.clone().sub(a))<0)z.negate();
  const x=new Vector3().crossVectors(y,z).normalize();
  if([[a,b,x],[b,a,x.clone().negate()],[a,c,z],[b,d,z],[a,e,y],[b,g,y]].some(([at,end,axis])=>ray(axis,at).dot(end.clone().sub(at).normalize())<Math.cos(2*Math.PI/180)))return null;
  return {focal,x,y,z};
 };
 const solved=solve(l,r,lf,rf,lu,ru);if(!solved)return null;
 // Reject ill-conditioned extrapolated vanishing points: one pixel at the
 // 800px analysis scale must not materially change lens/orientation.
 const points=[l,r,lf,rf,lu,ru];
 for(let i=0;i<points.length;i++)for(const axis of ['x','y'] as const)for(const sign of [-1,1]){
  const moved=points.map(v=>v.clone());moved[i][axis]+=sign*.00125*Math.max(1,aspect);
  const trial=solve(moved[0],moved[1],moved[2],moved[3],moved[4],moved[5]);
  if(!trial||Math.abs(trial.focal/solved.focal-1)>.15||trial.y.dot(solved.y)<.9986||trial.z.dot(solved.z)<.9986)return null;
 }
 const left=separation(selected,j.leftCorner)<=3,right=separation(selected,j.rightCorner)<=3;
 if(left===right)return null;
 const {focal,x,y,z}=solved;
 return {focal,basis:left?new Matrix4().makeBasis(x,y,z):new Matrix4().makeBasis(z,y,x.clone().negate()),approximate:true};
}
export function jointRoomWalls(j:JointCorners,selected:PhotoPoint):RoomWalls|null{
 if(!validJointCorners(j))return null;
 const left=separation(selected,j.leftCorner)<=3,right=separation(selected,j.rightCorner)<=3;if(left===right)return null;
 const upper=left?j.leftUpper:j.rightUpper;
 return {corner:left?j.leftCorner:j.rightCorner,left:left?j.leftFloor:j.leftCorner,right:left?j.rightCorner:j.rightFloor,upper,upperLeft:upper,upperRight:upper,floorOnly:true,jointCorners:j};
}
